'use client';

import { Button } from '@florencelabs/button';
import { MaterialSymbol } from '@florencelabs/icons';
import Link from 'next/link';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ProviderImage } from '@provider-directory/components/provider';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import {
  useDecisionTree,
  setSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { getProviderPrimaryPhoneNumber } from '@provider-directory/functions/get-available-phone';
import { joinProfessionalTitles } from '@provider-directory/functions/joinProfessionalTitles';
import { ProviderResource } from '@provider-directory/types/providers.contracts';
import { useAnalytics } from '../analytics/analytics-provider';
import {
  getNearestAvailability,
  groupCachedSlotsByLocation,
} from '../availability/availability.model';
import {
  useOrgContext,
  useOrgContextBasedOnHospitalAssociations,
} from '../org-context/org-context-provider';

interface RfvMismatchProviderCardProps {
  provider: ProviderResource;
}

export const RfvMismatchProviderCard = ({
  provider,
}: RfvMismatchProviderCardProps): ReactElement => {
  const { key } = useOrgContextBasedOnHospitalAssociations(
    provider.hospitalAssociations,
  );
  const orgContext = useOrgContext();
  const { t } = useTranslation(key);
  const { viewedProfileEvent } = useAnalytics();
  const { updateAppointment } = useAppointment();
  const {
    applyHistoryAndActivate,
    navigationHistory,
    setMismatchProviderOverride,
  } = useDecisionTree();

  const primarySpecialty = provider.specialties.find((s) => s.isPrimary);
  const specialtyName = primarySpecialty?.name || provider.specialties[0]?.name;

  const providerImage = provider.urls.find((url) => url.type === 'photo');

  const profileUrl = `/providers/${provider.npi}`;

  const phoneNumber = getProviderPrimaryPhoneNumber(
    provider,
    orgContext.getAssociationPhoneSelection(),
  );

  const handleViewProfileClick = (): void => {
    viewedProfileEvent({
      provider,
      phoneNumber,
      linkContextPage: 'SearchResults',
      linkContextComponent: 'DecisionTree',
      clickURL: profileUrl,
    });
  };

  const cachedSlots = groupCachedSlotsByLocation(provider.schedule);
  const nearestAvailability = getNearestAvailability(cachedSlots);

  const handleBookAppointment = (newProvider: ProviderResource) => {
    setSchedulingFlowType('BookAnotherProvider');
    updateAppointment({
      provider: newProvider,
      departmentId: nearestAvailability.closestSlot?.locationId || null,
      slotDate: nearestAvailability.closestSlot?.date || null,
    });

    setMismatchProviderOverride(newProvider);
    const historyWithoutErrorStep = navigationHistory.filter(
      (scene) => scene !== 'reason-for-visit-provider-error',
    );

    applyHistoryAndActivate(historyWithoutErrorStep, 'select-time-modal');
  };

  const providerNameWithTitles =
    provider.name.full + joinProfessionalTitles(provider.professionalTitles);

  return (
    <div className={'relative flex gap-4 rounded-lg bg-neutral-50 p-4'}>
      <Link
        href={profileUrl}
        className={'block h-20 w-20 flex-shrink-0'}
        data-testid={`view-profile-photo-${provider.npi}`}
        onClick={handleViewProfileClick}
      >
        <ProviderImage
          name={provider.name}
          imageUrl={providerImage?.value}
          className={'h-full w-full'}
          width={80}
          height={80}
        />
      </Link>

      <div className={'flex flex-1 flex-col gap-1'}>
        <Link
          href={profileUrl}
          data-testid={`view-profile-name-${provider.npi}`}
          onClick={handleViewProfileClick}
        >
          <h3 className={'text-body-medium-bold'}>{providerNameWithTitles}</h3>
        </Link>

        {specialtyName && (
          <p className={'text-body-small text-black'}>{specialtyName}</p>
        )}
        <div className={'mt-1 flex items-center gap-2 text-primary-color'}>
          <Button
            variant={'systemLink'}
            data-testid={`book-appointment-${provider.npi}`}
            className={'text-body-small-semibold !text-primary-color'}
            onClick={() => {
              handleBookAppointment(provider);
            }}
          >
            {t('schedule.bookAppointment')}
          </Button>
          {/* Desktop only: separator and view profile link */}
          <span className={'hidden text-black tablet:block'}>•</span>
          <Link
            href={profileUrl}
            className={'hidden text-body-small-semibold tablet:block'}
            data-testid={`view-profile-${provider.npi}`}
          >
            {t('schedule.viewProfile')}
          </Link>
        </div>
      </div>

      {/* Arrow button - mobile only, 44x44px, top right corner */}
      <Link
        href={profileUrl}
        className={
          'mr-[-12px] mt-[-12px] flex h-11 w-11 items-center justify-center tablet:hidden'
        }
        data-testid={`view-profile-arrow-${provider.npi}`}
        aria-label={t('aria.viewProfile', {
          providerName: providerNameWithTitles,
        })}
      >
        <MaterialSymbol name={'arrow_outward'} />
      </Link>
    </div>
  );
};
